import { Observable, combineLatest, switchMap, of, map, first } from 'rxjs';
import { SampleTypeProvider, TableService, TableTypeProvider } from '@domain/business-layer';
import { ExportedRecordMetadata, FileExporterTableMetadata, TableExpandableCards } from '../models';
import { DataRepository } from '@database/workspace';
import {
	ColumnModel,
	DateTimeService,
	ExpandableCard,
	ExpandableCardProperty,
	FormattingService,
	YesNoEnum
} from '@shared';
import { TimeDataFormattingService } from './time-data-formatting.service';

export abstract class BaseFileExporterService {
	public constructor(
		protected tableTypeProvider: TableTypeProvider,
		protected sampleTypeProvider: SampleTypeProvider,
		protected tableService: TableService,
		protected dataRepository: DataRepository,
		protected formattingService: FormattingService,
		protected dateTimeService: DateTimeService,
		protected timeDataFormattingService: TimeDataFormattingService
	) {}

	public fetchFileExportData$(
		tables: FileExporterTableMetadata[],
		exportedRecordMetadata: ExportedRecordMetadata
	): Observable<TableExpandableCards[]> {
		return combineLatest(
			tables.map((table: FileExporterTableMetadata) =>
				this.tableTypeProvider.isSampleTable$(table.tableName).pipe(
					switchMap((isSampleTable: boolean) =>
						isSampleTable ? this.sampleTypeProvider.getSampleTypes$() : of(null)
					),
					switchMap((sampleTypes: { [key: string]: boolean }) => {
						if (sampleTypes) {
							return this.tableService.getChildTableFieldsWithStandardsForReportPreview$(
								exportedRecordMetadata.workfileId,
								exportedRecordMetadata.projectNumber,
								exportedRecordMetadata.entityName,
								exportedRecordMetadata.entryId,
								table.tableName,
								table.isParent,
								sampleTypes
							);
						} else {
							return this.tableService.getChildTableFields$(
								exportedRecordMetadata.workfileId,
								exportedRecordMetadata.projectNumber,
								exportedRecordMetadata.entityName,
								exportedRecordMetadata.entryId,
								table.tableName,
								table.isParent,
								sampleTypes
							);
						}
					})
				)
			)
		).pipe(
			map((tablesExpandableCards: TableExpandableCards[]) =>
				tablesExpandableCards.map((tableExpandableCards: TableExpandableCards) => {
					if (tableExpandableCards.expandableCards?.length > 0) {
						return this.timeDataFormattingService.formatTimeFields(tableExpandableCards);
					}

					return this.handleTablesWithoutRecords(tableExpandableCards);
				})
			)
		);
	}

	public mapItemsInWorksheetFormat(
		expandableCardsProperties: ExpandableCardProperty[][]
	): { [key: string]: string }[] {
		return expandableCardsProperties.map((properties) => {
			const transformedObject: { [key: string]: string } = {};
			properties.forEach((property) => {
				transformedObject[property.key] = this.formattingService
					.transformValue(property.value, property.valueType)
					?.toString();
			});
			return transformedObject;
		});
	}

	private handleTablesWithoutRecords(tableExpandableCards: TableExpandableCards): TableExpandableCards {
		this.dataRepository
			.getColumnsMetadata$(tableExpandableCards.tableName)
			.pipe(first())
			.subscribe((columnModels: ColumnModel[]) => {
				const visibleColumns = columnModels.filter(
					(columnModel: ColumnModel) => columnModel.display_column === YesNoEnum.Yes
				);
				const properties = visibleColumns.map(
					(columnModel: ColumnModel) =>
						new ExpandableCardProperty({
							key: columnModel.column_name,
							value: '',
							label: columnModel.column_label,
							order: 0,
							valueType: '',
							compare: () => true
						})
				);
				const expandableCard: ExpandableCard = new ExpandableCard({
					id: 0,
					properties,
					isMaster: true,
					isExpanded: false
				});

				tableExpandableCards.expandableCards = [expandableCard];
			});

		return tableExpandableCards;
	}

	public abstract exportData(data: any[], fileName?: string): Promise<void>;
}
